"use client";

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  buildBookingRentalDataParamsKey,
  EMPTY_RENTAL_ADD_ONS,
  EMPTY_RENTAL_QUANTITIES,
  parseBookingRentalDataParamsKey,
} from "@/lib/booking/rental-data";
import { useAppSelector } from "@/lib/redux/hooks";
import { selectUserSelectedMiscellaneous } from "@/lib/redux/features/user-selected-details/userSelectedDetailsSlice";
import { bookingRentalService } from "@/lib/services/booking-rental-service";
import type { RentalChargesBreakdown } from "@/lib/types/booking-rental";

export function useBookingConfirmTotal() {
  const searchParams = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const miscellaneous = useAppSelector(selectUserSelectedMiscellaneous);

  const addOns = miscellaneous?.addOns ?? EMPTY_RENTAL_ADD_ONS;
  const quantities = miscellaneous?.quantities ?? EMPTY_RENTAL_QUANTITIES;

  const rentalDataParamsKey = useMemo(
    () => buildBookingRentalDataParamsKey(searchParams, addOns, quantities),
    [searchParamsKey, addOns, quantities],
  );

  const rentalDataParams = useMemo(
    () => parseBookingRentalDataParamsKey(rentalDataParamsKey),
    [rentalDataParamsKey],
  );

  const [bookingTotal, setBookingTotal] = useState<number | null>(null);
  const [priceBreakdown, setPriceBreakdown] =
    useState<RentalChargesBreakdown | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadTotal = async () => {
      if (!rentalDataParamsKey) {
        setBookingTotal(null);
        setPriceBreakdown(null);
        return;
      }

      const params = parseBookingRentalDataParamsKey(rentalDataParamsKey);
      if (!params) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response =
          await bookingRentalService.fetchBookingRentalData(params);

        if (!response.success) {
          throw new Error(response.message || "Unable to load price details.");
        }

        if (!isMounted) {
          return;
        }

        setPriceBreakdown(response.data.rental_charges_breakdown);
        setBookingTotal(response.data.rental_charges_breakdown.total.amount);
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        const message =
          loadError instanceof Error
            ? loadError.message
            : "Unable to load price details.";
        setError(message);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    void loadTotal();

    return () => {
      isMounted = false;
    };
  }, [rentalDataParamsKey]);

  const refreshTotal = async () => {
    if (!rentalDataParams) {
      setError("Missing required booking details to load price breakdown.");
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const response =
        await bookingRentalService.fetchBookingRentalData(rentalDataParams);

      if (!response.success) {
        throw new Error(response.message || "Unable to load price details.");
      }

      setPriceBreakdown(response.data.rental_charges_breakdown);
      setBookingTotal(response.data.rental_charges_breakdown.total.amount);
    } catch (loadError) {
      const message =
        loadError instanceof Error
          ? loadError.message
          : "Unable to load price details.";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    bookingTotal,
    priceBreakdown,
    isLoading,
    error,
    refreshTotal,
  };
}
